import {
  createStyles,
  makeStyles,
  Theme,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Box,
} from "@material-ui/core";
import React from "react";
import DataGrid from "../common/Table/newTable";
import { connect } from "react-redux";
import {
  LineupApiResponse,
  StackExposures,
} from "../preContest/queries/queryTypes";
import {
  APIPagination,
  AuthResponse,
  Filter,
  Filters,
  MultipleFilters,
  Pages,
  ResultsLoadError,
  SimulationTimestamp,
} from "./store/types";
import { getSimResults, setDisableProgressCheck, setFilter, setPageInfo, setResultsLoadError, setSimulatedSlate, setSimulationTimestamp } from "./store/actions";
import { Constants } from "../preContest/store/constants";
import { APIState } from "../utils/api/reducer";
import { APIConstants } from "../utils/api/constants";
import ProgressBar from "./progressBar";
import ResultsLoadErrorMessage from "./resultsLoadErrorMessage";
import { useLocalStorage } from "usehooks-ts";
import GetAppIcon from "@material-ui/icons/GetApp";
import { downloadCsvFile } from "../common/helpers/FilenameHelper";
import { useParams } from "react-router-dom";
import {
  preContestLineupDataGridHeaderConfig,
  preContestPlayerDataGridHeaderConfig,
  preContestFavoritedLineupDataGridHeaderConfig,
  preContestExposuresDataGridHeaderConfig,
  postContestStackDataGridHeaderConfig,
  preContestStackExposuresDataGridHeaderConfig,
  stackDataNFLGridHeaderConfig,
} from "../common/Table/newTableConfig";
import { showStackColumnsPreContest, showStackExposuresPreContest, showStackTabsPreContest, stackSports } from "../utils/stackConstants";
import {
  getExposuresKey,
  getExposuresKeyLineupPlayer,
  getSiteForShowdown,
} from "../utils/showdownHandler";
import { useGetSlateInfoQuery } from "../common/queries/slateQueries";
import { UpgradeModal } from "../common/LockIcon";
import { useUserId } from "../utils/authentication";
import EasyProjection from "../common/EasyProjection";
import { Projection } from "../common/EasyProjection/types";
import { isTeamSport } from "../utils/teamSportHelper";
import { SitesResponse, SlateResponse } from "../common/store/types";
import { StyledTabContainer, StyledTableWrapper } from "../postContest/_components";
import { getFilename } from "../common/helpers/FilenameHelper";
import EntryManager from "./EntryManager";
import { Stack, Typography } from "@mui/material";
import LineupPortfolioOverview from "../common/LineupPortfolioOverview";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiDataGrid-cell--editing': {
        backgroundColor: 'transparent',
      },
      "& .MuiTabs-root": {
        "& .Mui-disabled": {
          backgroundColor: "#555555 !important",
          color: "#BEBEBE",
          cursor: "pointer",
        },
      },
      '& .MuiTabs-indicator': {
        backgroundColor: 'rgba(1, 90, 206, 1)'
      },
      width: "100%",
      maxHeight: "100%",
      height: "auto",
      margin: "10px 0",
    },
    tab: {
      marginRight: '8px',
      borderRadius: "3px 3px 0px 0px",
      fontSize: '12px',
      fontWeight: 400,
      fontFamily: 'Inter',
    },
    active: {
      fontWeight: 800,
    },
    projTab: {
      marginRight: 64,
    },
    exportButton: {
      padding: '6px 12px',
      borderRadius: 6,
      backgroundColor: "#0042C1",
      "& a": {
        color: "white !important",
        textDecoration: "none",
        display: "flex",
        alignItems: "center",
      },
      "&:hover": {
        color: "white",
        backgroundColor: "#0042C1",
      },
      "& .MuiSvgIcon-root": {
        marginLeft: 4,
        fontSize: "1rem",
      },
    },
    settingsWrapper: {
      borderTop: "1px solid rgba(251,255,57, .3)",
      borderRight: "1px solid rgba(251,255,57, .3)",
      borderLeft: "1px solid rgba(251,255,57, .3)",
      borderTopLeftRadius: "5px",
      borderTopRightRadius: "5px",
      padding: 4,
    },
  })
);

interface ReduxProps {
  readonly selectedSite: SitesResponse;
  readonly filters: Filters;
  readonly pageInfo: Pages;
  readonly playerAllRoi: APIState<LineupApiResponse[]>;
  readonly lineupAllRoi: APIState<LineupApiResponse[]>;
  readonly stackRoi: APIState<LineupApiResponse[]>;
  readonly contestPlayers: APIState<LineupApiResponse[]>;
  readonly pagination: APIPagination;
  readonly disableProgressCheck: boolean;
  readonly totalEntries: number;
  readonly simulatedSlate: SlateResponse;
  readonly selectedSlate: SlateResponse;
  readonly authenticated: APIState<AuthResponse>;
  readonly lineupFile: string;
  readonly percentToFirst: number;
  readonly lineupVerified: boolean;
  readonly simCount: number;
  readonly isParsingCsv: boolean;
  readonly resultsLoadError: ResultsLoadError | null;
}

interface DispatchProps {
  readonly SetFilter: (
    filter: Filter | MultipleFilters,
    type:
      | Constants.SET_USER_SORT
      | Constants.SET_PLAYER_SORT
      | Constants.SET_LINEUP_SORT
      | Constants.SET_STACK_SORT
  ) => void;
  readonly SetPageInfo: (pageInfo: Pages) => void;
  readonly SetDisableProgressCheck: (newValue: boolean) => void;
  readonly SetSimulatedSlate: (slate: SlateResponse) => void;
  readonly SetSimulationTimestamp: (timestamp: SimulationTimestamp) => void;
  readonly SetResultsLoadError: (error: ResultsLoadError | null) => void;
  readonly GetSimResults: (slateId: string, user: string, filters: Filters, pageInfo: Pages, sport: string, includeStacks: boolean) => void;
}

type Props = ReduxProps & DispatchProps;

const TabsWrapper = (props: Props) => {
  const classes = useStyles();
  const [value, setValue] = React.useState("projections");
  const [csvHeaders, setCsvHeaders] = React.useState("");
  const [simulateCallRunning, setSimulateCallRunning] = React.useState(false);
  const [loadingPct, setLoadingPct] = React.useState("0");
  const [stackType, setStackType] = React.useState<string>("playerExp");
  const [updatedPlayerRoiList, setUpdatedPlayerRoiList] = React.useState<{
    [key: string]: number;
  }>({});
  const [updatedStackRoiList, setUpdatedStackRoiList] = React.useState<{
    [key: string]: number;
  }>({});
  const [openModal, setOpenModal] = React.useState(false);
  const userId = useUserId();
  const auth = 'awe' + userId;
  const routeParams = useParams<{ league: string }>();
  const interval = React.useRef<ReturnType<typeof setInterval>>();
  const prevLineupStatus = React.useRef<string | null>(null);
  const [lockTier, setLockTier] = React.useState(false);
  
  const {
    selectedSite,
    selectedSlate,
    playerAllRoi,
    lineupAllRoi,
    stackRoi,
    contestPlayers,
    lineupFile,
    percentToFirst,
    SetDisableProgressCheck,
    SetSimulatedSlate,
    SetSimulationTimestamp,
    SetResultsLoadError,
    GetSimResults,
    disableProgressCheck,
    totalEntries,
    simulatedSlate,
    authenticated,
    lineupVerified,
    simCount,
    isParsingCsv,
    resultsLoadError,
    filters,
    pageInfo
  } = props;
  const savedROIBoostKey = `preContest-roiBoost-${simulatedSlate.sport}-${simulatedSlate.site}-${simulatedSlate.slateId}`;
  const savedStackBoostKey = `preContest-stackBoost-${simulatedSlate.sport}-${simulatedSlate.site}-${simulatedSlate.slateId}`;
  const savedROIExposureKey = `preContest-roiExposure-${simulatedSlate.sport}-${simulatedSlate.site}-${simulatedSlate.slateId}`;
  const savedStackExposureKey = `preContest-stackExposure-${simulatedSlate.sport}-${simulatedSlate.site}-${simulatedSlate.slateId}`;
  const [disableUpdateInfo, setDisableUpdateInfo] = React.useState(false);
  const { data: updateInfo } = useGetSlateInfoQuery(selectedSlate?.slateId?.toString(), disableUpdateInfo || selectedSlate?.slateId === undefined)

  const handleChange = (event: React.ChangeEvent<{}>, newValue: any) => {
    setValue(newValue);
  };

  const [savedLineups, setSavedLineups] = useLocalStorage<LineupApiResponse[]>(
    `precontest-lineups-${selectedSlate.slateId}`,
    []
  );

  const [playerGroups, setPlayerGroups] = useLocalStorage<{ id: string; players: LineupApiResponse[]; roiBoost: number }[]>(
    `precontest-playerGroups-${simulatedSlate.slateId}`,
    []
  );

  const handleSimulate = async (projections: Projection[]) => {
    const request = {
      user: auth,
      lineupsData: lineupFile,
      slateId: selectedSlate.slateId,
      contestType: "PreContest",
      uploadData: true,
      totalEntries,
      percentToFirst,
      projections,
      simulationCount: simCount
    }
    setSimulateCallRunning(true)
    SetResultsLoadError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contests/simulateLambdaContest`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`Simulation request failed with status ${response.status}`);
      }

      // Track simulation start time for timeout calculation
      SetSimulationTimestamp({
        startTime: Date.now()
      });

      SetSimulatedSlate(selectedSlate);
      SetDisableProgressCheck(false);
      setSimulateCallRunning(false);
      setSavedLineups([]);
    } catch (error) {
      console.error('Failed to start simulation:', error);
      setSimulateCallRunning(false);
      // Don't set disableProgressCheck to false on error - keep it at current state
      alert('Failed to start simulation. Please try again.');
    }
  };
  

  const onSetProjections = (projections: Projection[]) => {
    handleSimulate(projections);
    setValue('lineups');
  }

  const setFavoriteLineup = (lineup: LineupApiResponse) => {
    const tierLocked = false;
    if (!tierLocked) {
      let alreadySavedLineups = savedLineups;
      if (alreadySavedLineups.some((x) => x.fullLineup === lineup.fullLineup)) {
        alreadySavedLineups = alreadySavedLineups.filter(
          (x) => x.fullLineup !== lineup.fullLineup
        );
        setSavedLineups(alreadySavedLineups);
      } else {
        alreadySavedLineups.push(lineup);
        const sortedLineups = alreadySavedLineups?.sort((a, b) => b.simLineupRoi - a.simLineupRoi);
        setSavedLineups(sortedLineups);
      }
    } else {
      setOpenModal(true);
    }
  };

  React.useEffect(() => {
    const saveCsvHeaders = async () => {
      let csvData = "";
      const header = await fetch(
        `${process.env.REACT_APP_API_URL}/api/contests/csvHeaders?sport=${
          simulatedSlate?.sport
        }&site=${
          simulatedSlate?.type?.toUpperCase() === "SHOWDOWN"
            ? getSiteForShowdown(simulatedSlate?.site)
            : simulatedSlate?.site
        }`
      ).then((res) => res.json());
      csvData = header + "\n";
      setCsvHeaders(csvData);
    };
    if (simulatedSlate?.sport && simulatedSlate?.site) {
      saveCsvHeaders();
    }

  }, [simulatedSlate]);

  const getCsvData = (): string => {
    let csvData = csvHeaders;
    if (savedLineups?.length > 0 && simulatedSlate?.sport && simulatedSlate?.type) {
      savedLineups.forEach((x) => {
        if (x.exportableLineup) {
          csvData = csvData.concat(x.exportableLineup + "\n");
        }
      });
    }
    return csvData
  };

  React.useEffect(() => {
    const savedPlayerROIBoosts = localStorage?.getItem(savedROIBoostKey);
    const savedStackROIBoosts = localStorage?.getItem(savedStackBoostKey);
    if(savedPlayerROIBoosts) {
      const playerROIBoosts = JSON.parse(savedPlayerROIBoosts);
      setUpdatedPlayerRoiList(playerROIBoosts);
    } else {
      setUpdatedPlayerRoiList({});
    }
    if(savedStackROIBoosts) {
      const stackROIBoosts = JSON.parse(savedStackROIBoosts);
      setUpdatedStackRoiList(stackROIBoosts);
    } else {
      setUpdatedStackRoiList({});
    }
  }, [selectedSite, simulatedSlate]);

  React.useEffect(() => {
    if(updateInfo && selectedSlate?.slateId?.toString()){
      setDisableUpdateInfo(true);
    }
  }, [updateInfo]);

  const exposures = (filter: boolean = true) => {
    let players = contestPlayers.data;
    if (
      savedLineups?.length > 0 &&
      contestPlayers.status === APIConstants.SUCCEEDED
    ) {
      const playerCounts: { [key: string]: number } = {};
      savedLineups.forEach((x) =>
        x.fullLineup
          .split(",")
          .forEach(
            (x, index) =>
              (playerCounts[
                getExposuresKeyLineupPlayer(
                  x.trim(),
                  index,
                  simulatedSlate?.type,
                  simulatedSlate?.sport,
                  simulatedSlate?.site
                )
              ] = playerCounts[
                getExposuresKeyLineupPlayer(
                  x.trim(),
                  index,
                  simulatedSlate?.type,
                  simulatedSlate?.sport,
                  simulatedSlate?.site
                )
              ]
                ? playerCounts[
                    getExposuresKeyLineupPlayer(
                      x.trim(),
                      index,
                      simulatedSlate?.type,
                      simulatedSlate?.sport,
                      simulatedSlate?.site
                    )
                  ] + 1
                : 1)
          )
      );

      players?.forEach((x) => {
        if (playerCounts[getExposuresKey(x)]) {
          x.exposure =
            (playerCounts[getExposuresKey(x)] / savedLineups.length) * 100;
          x.exposurePct =
            (
              (playerCounts[getExposuresKey(x)] / savedLineups.length) *
              100
            ).toFixed(2) + "%";
          x.leverage =
            (parseInt(x.exposurePct) / 100 - x.overallOwnership) * 100;
          x.leveragePct =
            (
              (parseInt(x.exposurePct) / 100 - x.overallOwnership) *
              100
            ).toFixed(1) + "%";
        }
        else {
          x.exposure = 0;
          x.exposurePct = "0%";
          x.leverage = (parseInt(x.exposurePct) / 100 - x.overallOwnership) * 100;
          x.leveragePct = 
          (
            (parseInt(x.exposurePct) / 100 - x.overallOwnership) *
            100
          ).toFixed(1) + "%";
        }
      });
      if (filter) {
        return players.filter((x) => x.exposure);
      } else {
        return players;
      }
    } else if (
      savedLineups?.length === 0 &&
      contestPlayers.status === APIConstants.SUCCEEDED
    ) {
      players?.forEach((x) => {
        x.exposure = 0;
        x.exposurePct = "0%";
      });
    }

    if (!filter) {
      return players;
    }

    return [];
  };

  const stacks = () => {
    const stackExposuresDict: { [key: string]: StackExposures } = {};
    const savedLineupsCount = savedLineups.length;

    lineupAllRoi.status === APIConstants.SUCCEEDED &&
      lineupAllRoi?.data?.forEach((lineup) => {
        lineup?.stacks.forEach((stack) => {
          var exp = {
            ThreeMLBOneNFLStackExposureCount: 0,
            FourMLBTwoNFLStackExposureCount: 0,
            FiveMLBThreeNFLStackExposureCount: 0,
          } as StackExposures;

          if (stackExposuresDict[stack.team]) {
            exp = stackExposuresDict[stack.team];
          }

          if (
            (simulatedSlate.sport?.toLowerCase() === "mlb" && stack.count > 2) ||
            (['cfb', 'nfl'].includes(simulatedSlate.sport?.toLowerCase()) && stack.count > 0)
          ) {
            stackExposuresDict[stack.team] = exp;
          }
        });
      });

    savedLineups.forEach((lineup) => {
      lineup?.stacks.forEach((stack) => {
        var exp = {
          QbName: stack.qbName,
          ThreeMLBOneNFLStackExposureCount: 0,
          FourMLBTwoNFLStackExposureCount: 0,
          FiveMLBThreeNFLStackExposureCount: 0,
        } as StackExposures;

        if (stackExposuresDict[stack.team]) {
          exp = stackExposuresDict[stack.team];
        }
        const stackAndSport = `${stack.count}|${simulatedSlate?.sport}`;
        switch (stackAndSport) {
          case "1|NFL":
          case "1|CFB":
          case "3|MLB":
            exp.ThreeMLBOneNFLStackExposureCount =
              exp.ThreeMLBOneNFLStackExposureCount + 1;
            break;
          case "2|NFL":
          case "2|CFB":
          case "4|MLB":
            exp.FourMLBTwoNFLStackExposureCount =
              exp.FourMLBTwoNFLStackExposureCount + 1;
            break;
          case "3|NFL":
          case "3|CFB":
          case "5|MLB":
            exp.FiveMLBThreeNFLStackExposureCount =
              exp.FiveMLBThreeNFLStackExposureCount + 1;
            break;
          default:
            break;
        }
        if (
          (simulatedSlate?.sport?.toLowerCase() === "mlb" && stack.count > 2) ||
          (['nfl', 'cfb'].includes(simulatedSlate?.sport?.toLowerCase()) && stack.count > 0)
        ) {
          stackExposuresDict[stack.team] = exp;
        }
      });
    });
    if (Object.keys(stackExposuresDict).length > 0) {
      const stacks = Object.keys(stackExposuresDict).map((team) => {
        const stackCounts = stackExposuresDict[team];

        return {
          id: team,
          team: team,
          ThreeMLBOneNFLStackExposureCount:
            stackCounts.ThreeMLBOneNFLStackExposureCount,
          ThreeMLBOneNFLStackExposurePct:
            stackCounts.ThreeMLBOneNFLStackExposureCount / savedLineupsCount,
          FourMLBTwoNFLStackExposureCount:
            stackCounts.FourMLBTwoNFLStackExposureCount,
          FourMLBTwoNFLStackExposurePct:
            stackCounts.FourMLBTwoNFLStackExposureCount / savedLineupsCount,
          FiveMLBThreeNFLStackExposureCount:
            stackCounts.FiveMLBThreeNFLStackExposureCount,
          FiveMLBThreeNFLStackExposurePct:
            stackCounts.FiveMLBThreeNFLStackExposureCount / savedLineupsCount,
        } as StackExposures;
      });

      return stacks;
    }

    return [];
  };

  const stackExposures = () => {
    const stackExposuresDict: { [key: string]: StackExposures } = {};
    const savedLineupsCount = savedLineups.length;
    savedLineups.forEach((lineup) => {
      lineup?.stacks.forEach((stack) => {
        var exp = {
          QbName: stack.qbName,
          ThreeMLBOneNFLStackExposureCount: 0,
          FourMLBTwoNFLStackExposureCount: 0,
          FiveMLBThreeNFLStackExposureCount: 0,
        } as StackExposures;
        if (stackExposuresDict[stack.team]) {
          exp = stackExposuresDict[stack.team];
        }
        const stackAndSport = `${stack.count}|${simulatedSlate?.sport}`;
        switch (stackAndSport) {
          case "1|NFL":
          case "1|CFB":
          case "3|MLB":
            exp.ThreeMLBOneNFLStackExposureCount =
              exp.ThreeMLBOneNFLStackExposureCount + 1;
            break;
          case "2|NFL":
          case "2|CFB":
          case "4|MLB":
            exp.FourMLBTwoNFLStackExposureCount =
              exp.FourMLBTwoNFLStackExposureCount + 1;
            break;
          case "3|NFL":
          case "3|CFB":
          case "5|MLB":
            exp.FiveMLBThreeNFLStackExposureCount =
              exp.FiveMLBThreeNFLStackExposureCount + 1;
            break;
          default:
            break;
        }
        if (['nfl', 'cfb'].includes(simulatedSlate?.sport?.toLowerCase()) || stack.count > 2) {
          stackExposuresDict[stack.team] = exp;
        }
      });
    });

    if (Object.keys(stackExposuresDict).length > 0) {
      const stacks = Object.keys(stackExposuresDict).map((team) => {
        const stackCounts = stackExposuresDict[team];
        const stackOpponent = stackRoi.data?.find(x => x.teamName === team)?.opponentTeamName;
        return {
          id: team,
          team: team,
          opponentTeamName: stackOpponent,
          qbName: stackCounts.QbName,
          ThreeMLBOneNFLStackExposureCount:
            stackCounts.ThreeMLBOneNFLStackExposureCount,
          ThreeMLBOneNFLStackExposurePct:
            stackCounts.ThreeMLBOneNFLStackExposureCount / savedLineupsCount,
          FourMLBTwoNFLStackExposureCount:
            stackCounts.FourMLBTwoNFLStackExposureCount,
          FourMLBTwoNFLStackExposurePct:
            stackCounts.FourMLBTwoNFLStackExposureCount / savedLineupsCount,
          FiveMLBThreeNFLStackExposureCount:
            stackCounts.FiveMLBThreeNFLStackExposureCount,
          FiveMLBThreeNFLStackExposurePct:
            stackCounts.FiveMLBThreeNFLStackExposureCount / savedLineupsCount,
        } as StackExposures;
      });
      console.log('stacks', stacks)
      return stacks;
    }

    return [];
  };

  React.useEffect(() => {
    if (!disableProgressCheck && simulatedSlate.sport) {
      setLoadingPct("0");
      let loadingTime =
        loadingTimeDict[
          `${simulatedSlate?.sport?.toUpperCase()}-${totalEntries}`
        ];
      if (simulatedSlate.type?.toUpperCase() === "SHOWDOWN") {
        loadingTime = loadingTime / 2;
      }
      let totalMilliseconds = 0;
      interval.current = setInterval(() => {
        if (!disableProgressCheck) {
          totalMilliseconds += 10;
          setLoadingPct(
            ((totalMilliseconds * 0.98 * 100) / loadingTime).toFixed(0)
          );
        }
      }, 10);
      setTimeout(() => {
        clearInterval(interval.current);
      }, loadingTime);
    } else {
      clearInterval(interval.current);
      setLoadingPct("0");
    }
  }, [disableProgressCheck]);

  // Stop showing progress bar when lineup data has loaded (only on transition from STARTED to SUCCEEDED).
  // If the load fails instead (e.g. a large result outlasting the request timeout), show a retry message
  // rather than leaving the bar at 98%.
  React.useEffect(() => {
    if (prevLineupStatus.current === APIConstants.STARTED && !disableProgressCheck) {
      if (lineupAllRoi.status === APIConstants.SUCCEEDED) {
        SetDisableProgressCheck(true);
        SetResultsLoadError(null);
      } else if (lineupAllRoi.status === APIConstants.FAILED) {
        SetResultsLoadError('resultsFailed');
      }
    }
    prevLineupStatus.current = lineupAllRoi.status;
  }, [lineupAllRoi.status, disableProgressCheck, SetDisableProgressCheck, SetResultsLoadError]);

  // Retry from where the load stopped: refetch a finished sim's results, or resume polling a sim that hadn't
  // reported done
  const retryResultsLoad = () => {
    if (resultsLoadError === 'statusTimedOut') {
      SetSimulationTimestamp({ startTime: Date.now() });
    } else {
      GetSimResults(selectedSlate.slateId, auth, filters, pageInfo, selectedSlate.sport, stackSports.includes(routeParams.league?.toLowerCase()));
    }
    SetResultsLoadError(null);
  };

  const loadingTimeDict: { [key: string]: number } = {
    "MLB-250": 30000,
    "MLB-500": 45000,
    "MLB-1000": 70000,
    "MLB-1500": 60000,
    "MLB-2000": 70000,
    "MLB-5000": 70000,
    "MLB-10000": 70000,
    "MLB-15000": 90000,
    "MLB-20000": 100000,
    "MLB-25000": 110000,
    "PGA-250": 30000,
    "PGA-500": 45000,
    "PGA-1000": 70000,
    "PGA-1500": 60000,
    "PGA-2000": 70000,
    "PGA-5000": 70000,
    "PGA-10000": 70000,
    "PGA-15000": 90000,
    "PGA-20000": 100000,
    "PGA-25000": 110000,
    "NBA-250": 30000,
    "NBA-500": 45000,
    "NBA-1000": 70000,
    "NBA-1500": 60000,
    "NBA-2000": 70000,
    "NBA-5000": 70000,
    "NBA-10000": 70000,
    "NBA-15000": 90000,
    "NBA-20000": 100000,
    "NBA-25000": 110000,
    "NHL-250": 30000,
    "NHL-500": 45000,
    "NHL-1000": 70000,
    "NHL-1500": 60000,
    "NHL-2000": 70000,
    "NHL-5000": 70000,
    "NHL-10000": 70000,
    "NHL-15000": 90000,
    "NHL-20000": 100000,
    "NHL-25000": 110000,
    "NFL-250": 30000,
    "NFL-500": 45000,
    "NFL-1000": 70000,
    "NFL-1500": 60000,
    "NFL-2000": 70000,
    "NFL-5000": 70000,
    "NFL-10000": 70000,
    "NFL-15000": 90000,
    "NFL-20000": 100000,
    "NFL-25000": 120000,
    "NFL-50000": 120000,
    "MMA-250": 30000,
    "MMA-500": 45000,
    "MMA-1000": 70000,
    "MMA-1500": 60000,
    "MMA-2000": 70000,
    "MMA-5000": 70000,
    "MMA-10000": 70000,
    "MMA-15000": 90000,
    "MMA-20000": 100000,
    "MMA-25000": 90000,
    "MMA-50000": 90000,
    "NASCAR-250": 30000,
    "NASCAR-500": 45000,
    "NASCAR-1000": 70000,
    "NASCAR-1500": 60000,
    "NASCAR-2000": 70000,
    "NASCAR-5000": 70000,
    "NASCAR-10000": 70000,
    "NASCAR-15000": 90000,
    "NASCAR-20000": 100000,
    "NASCAR-25000": 110000,
    "CFB-250": 30000,
    "CFB-500": 45000,
    "CFB-1000": 70000,
    "CFB-1500": 60000,
    "CFB-2000": 70000,
    "CFB-5000": 70000,
    "CFB-10000": 70000,
    "CFB-15000": 90000,
    "CFB-20000": 100000,
    "CFB-25000": 110000,
  };

  React.useEffect(() => {
    if (savedLineups.length > 0) {
      stackExposures();
    }
  }, [savedLineups]);

  React.useEffect(() => {
    setLockTier(
      false
    );
  }, [authenticated]);


  const footer = () => {
    return (
        <Stack direction='row' justifyContent='space-between' width='100%' sx={{ borderTop: '1px solid rgba(1, 90, 206, 0.30)', p: '10px 12px' }}>
          <LineupPortfolioOverview />
          <Stack direction ='row' gap='15px' py={'10px'}>
            <EntryManager buttonLabel='Add to Entry Manger' buttonVariant='outlined' showLineupCount />
            <Button
              className={classes.exportButton}
              disabled={savedLineups?.length === 0}
              onClick={() => downloadCsvFile(getCsvData(), getFilename(selectedSlate, `_Pre_Contest_Sims_Lineups`))}
            >
              Export Favorites {savedLineups.length > 0 ? `(${savedLineups?.length})` : ''} <GetAppIcon fontSize="small" />
            </Button>
          </Stack>
        </Stack>
    )
  }
  return (
    <>
      <UpgradeModal open={openModal} onClose={() => setOpenModal(false)} />
      <StyledTabContainer className={classes.root}>
        <Tabs
          value={value}
          indicatorColor="primary"
          textColor="inherit"
          onChange={handleChange}
        >
          <Tab
            value="projections"
            label="Projections"
            className={`${classes.tab} ${classes.projTab} ${
              value === "projections" ? classes.active : ""
            }`}
          ></Tab>
          <Tab
            value="lineups"
            label="Lineups"
            className={`${classes.tab} ${
              value === "lineups" ? classes.active : ""
            }`}
          ></Tab>
          <Tab
            value="player"
            label="Player ROI"
            className={`${classes.tab} ${
              value === "player" ? classes.active : ""
            }`}
          ></Tab>
          {showStackTabsPreContest(simulatedSlate) && (
            <Tab
              value="stack"
              label="Stack ROI"
              className={`${classes.tab} ${
                value === "stack" ? classes.active : ""
              }`}
            ></Tab>
          )}
          <Tab
            value="favorites"
            label="Favorites"
            className={`${classes.tab} ${
              value === "favorites" ? classes.active : ""
            }`}
          ></Tab>
          <Tab
            value="exposures"
            label="Exposures"
            className={`${classes.tab} ${
              value === "exposures" ? classes.active : ""
            }`}
          ></Tab>
        </Tabs>
        <StyledTableWrapper>
          {isParsingCsv && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
              width="100%"
            >
              <CircularProgress size={48} style={{ color: 'white', marginBottom: 16 }} />
              <span style={{ color: 'white', fontSize: '16px' }}>Parsing lineup file...</span>
            </Box>
          )}
          {!isParsingCsv && value === "projections" && (
            <EasyProjection selectedSlate={selectedSlate} onSetProjections={onSetProjections} disabled={!lineupFile || !lineupVerified} />
          )}
          {resultsLoadError && !disableProgressCheck && !simulateCallRunning
            ? <ResultsLoadErrorMessage error={resultsLoadError} onRetry={retryResultsLoad} />
            : (!disableProgressCheck || simulateCallRunning) && <ProgressBar percentage={loadingPct} />}
          {disableProgressCheck && value === "lineups" && (
            <DataGrid
              includeStacks={showStackExposuresPreContest(simulatedSlate)}
              lineups={lineupAllRoi.data}
              players={exposures(false)}
              headers={preContestLineupDataGridHeaderConfig(
                setFavoriteLineup,
                savedLineups,
                updatedPlayerRoiList,
                updatedStackRoiList,
                simulatedSlate,
                csvHeaders,
                lockTier,
                playerGroups
              )}
              isLoading={lineupAllRoi?.status === APIConstants.STARTED}
              selectedView="lineups"
              setPlayerList={(playerList) =>{
                localStorage.setItem(savedROIBoostKey, JSON.stringify(playerList));
                setUpdatedPlayerRoiList(playerList)
              }
              }
              setStackList={(stackList) => {
                localStorage.setItem(savedStackBoostKey, JSON.stringify(stackList));
                setUpdatedStackRoiList(stackList)
              }}
              playerList={updatedPlayerRoiList}
              stackList={updatedStackRoiList}
              savedROIExposureKey={savedROIExposureKey}
              savedStackExposureKey={savedStackExposureKey}
              simType="pre"
              setStackView={(view: string) => console.log("")}
              stacks={stacks()}
              tierlocked={lockTier}
              footer={() => footer()}
              backgroundColor='rgba(1, 90, 206, 0.15)'
              playerGroups={playerGroups}
              setPlayerGroups={setPlayerGroups}
            />
          )}
          {disableProgressCheck && value === "player" && (
            <DataGrid
              includeStacks={showStackExposuresPreContest(simulatedSlate)}
              lineups={playerAllRoi.data}
              players={exposures(false)}
              headers={preContestPlayerDataGridHeaderConfig(
                isTeamSport(simulatedSlate?.sport)
              )}
              isLoading={playerAllRoi.status === APIConstants.STARTED}
              selectedView="player"
              setPlayerList={(playerList) => {
                localStorage.setItem(savedROIBoostKey, JSON.stringify(playerList));
                setUpdatedPlayerRoiList(playerList)
              }
              }
              setStackList={(stackList) => {
                localStorage.setItem(savedStackBoostKey, JSON.stringify(stackList));
                setUpdatedStackRoiList(stackList)
              }}
              playerList={updatedPlayerRoiList}
              stackList={updatedStackRoiList}
              simType="pre"
              setStackView={(view: string) => console.log("")}
              stacks={stacks()}
              tierlocked={lockTier}
              backgroundColor='rgba(1, 90, 206, 0.15)'
              playerGroups={playerGroups}
              setPlayerGroups={setPlayerGroups}
            />
          )}
          {disableProgressCheck && value === "stack" && (
            <DataGrid
              includeStacks={showStackExposuresPreContest(simulatedSlate)}
              lineups={stackRoi.data}
              players={exposures(false)}
              headers={
                routeParams.league?.toLowerCase() === "mlb"
                  ? postContestStackDataGridHeaderConfig
                  : stackDataNFLGridHeaderConfig
              }
              isLoading={stackRoi.status === APIConstants.STARTED}
              selectedView="stack"
              setPlayerList={(playerList) => {
                localStorage.setItem(savedROIBoostKey, JSON.stringify(playerList));
                setUpdatedPlayerRoiList(playerList)
              }}
              setStackList={(stackList) => {
                localStorage.setItem(savedStackBoostKey, JSON.stringify(stackList));
                setUpdatedStackRoiList(stackList)
              }}
              playerList={updatedPlayerRoiList}
              stackList={updatedStackRoiList}
              simType="pre"
              setStackView={(view: string) => console.log("")}
              stacks={stacks()}
              tierlocked={lockTier}
              backgroundColor='rgba(1, 90, 206, 0.15)'
            />
          )}
          {disableProgressCheck && value === "favorites" && (
            <DataGrid
              lineups={savedLineups}
              players={exposures(false)}
              includeStacks={showStackExposuresPreContest(simulatedSlate)}
              headers={preContestFavoritedLineupDataGridHeaderConfig(
                setFavoriteLineup,
                savedLineups,
                showStackColumnsPreContest(simulatedSlate),
                simulatedSlate?.sport
              )}
              isLoading={lineupAllRoi.status === APIConstants.STARTED}
              emptyText="No Lineups have been favorited"
              selectedView="favorites"
              setPlayerList={(playerList) => {
                localStorage.setItem(savedROIBoostKey, JSON.stringify(playerList));
                setUpdatedPlayerRoiList(playerList)
              }
              }
              setStackList={(stackList) => {
                localStorage.setItem(savedStackBoostKey, JSON.stringify(stackList));
                setUpdatedStackRoiList(stackList)
              }}
              playerList={updatedPlayerRoiList}
              stackList={updatedStackRoiList}
              simType="pre"
              setStackView={(view: string) => console.log("")}
              stacks={stacks()}
              tierlocked={lockTier}
              backgroundColor='rgba(1, 90, 206, 0.15)'
            />
          )}
          {disableProgressCheck && value === "exposures" && (
            <DataGrid
              includeStacks={showStackExposuresPreContest(simulatedSlate)}
              lineups={
                stackType === "playerExp" ? exposures() : stackExposures()
              }
              players={exposures(false)}
              headers={
                stackType === "playerExp"
                  ? preContestExposuresDataGridHeaderConfig(
                      simulatedSlate?.sport === "PGA"
                    )
                  : preContestStackExposuresDataGridHeaderConfig(
                      ['nfl', 'cfb'].includes(simulatedSlate?.sport?.toLowerCase()),
                      lineupAllRoi.data
                    )
              }
              isLoading={contestPlayers.status === APIConstants.STARTED}
              selectedView={stackType}
              emptyText="No Lineups have been favorited"
              setPlayerList={(playerList) => {
                localStorage.setItem(savedROIBoostKey, JSON.stringify(playerList));
                setUpdatedPlayerRoiList(playerList)
              }
              }
              setStackList={(stackList) => {
                localStorage.setItem(savedStackBoostKey, JSON.stringify(stackList));
                setUpdatedStackRoiList(stackList)
              }}
              playerList={updatedPlayerRoiList}
              stackList={updatedStackRoiList}
              simType="pre"
              setStackView={(view: string) => setStackType(view)}
              stacks={stacks()}
              tierlocked={lockTier}
              backgroundColor='rgba(1, 90, 206, 0.15)'
              playerGroups={playerGroups}
              setPlayerGroups={setPlayerGroups}
              allLineups={lineupAllRoi.data}
            />
          )}
        </StyledTableWrapper>
      </StyledTabContainer>
    </>
  );
};
const mapStateToProps = (state: any) => {
  const {
    filters,
    pageInfo,
    playerAllRoi,
    lineupAllRoi,
    stackRoi,
    pagination,
    contestPlayers,
    disableProgressCheck,
    totalEntries,
    simulatedSlate,
    lineupFile,
    percentToFirst,
    lineupVerified,
    simCount,
    isParsingCsv,
    resultsLoadError
  } = state.preContest;

  const {
    selectedSite,
    selectedSlate
  } = state.common;

  const {
    authenticated
  } = state.common;
  return {
    selectedSite,
    filters,
    pageInfo,
    playerAllRoi,
    lineupAllRoi,
    stackRoi,
    pagination,
    contestPlayers,
    disableProgressCheck,
    totalEntries,
    simulatedSlate,
    selectedSlate,
    authenticated,
    lineupFile,
    percentToFirst,
    lineupVerified,
    simCount,
    isParsingCsv,
    resultsLoadError
  };
};

const mapDispatchToProps = {
  SetFilter: setFilter,
  SetPageInfo: setPageInfo,
  SetDisableProgressCheck: setDisableProgressCheck,
  SetSimulatedSlate: setSimulatedSlate,
  SetSimulationTimestamp: setSimulationTimestamp,
  SetResultsLoadError: setResultsLoadError,
  GetSimResults: getSimResults,
};

export default connect(mapStateToProps, mapDispatchToProps)(TabsWrapper);
